import {
  RecordingGuideIllustration,
  UpdateRecordingIllustration,
} from '@carboncopyco/illustrations';
import { Button, Modal, TextBase, TextSubHeader } from '@carboncopyco/ui';
import { type ReactNode, useState } from 'react';

const DONT_SHOW_AGAIN_KEY = 'dontShowThisAgain';

export enum RecorderModes {
  play = 'play',
  record = 'record',
  recordTab = 'recordTab',
  updateRecording = 'updateRecording',
  updateTab = 'updateTab',
  externalRecording = 'externalRecording',
}

interface RecordingGuideModalProps {
  onClose: () => void;
  onConfirm: () => void;
  isOpen: boolean;
  mode: RecorderModes;
  timeLimitText?: ReactNode;
}

const getDontShowAgain = (): RecorderModes[] => {
  return JSON.parse(localStorage.getItem(DONT_SHOW_AGAIN_KEY) || '[]') || [];
};

export const isDisabledRecordingGuideModal = (mode: RecorderModes): boolean => {
  const localStorageList = getDontShowAgain();
  return localStorageList.includes(mode);
};

export const RecordingGuideModal = ({
  onClose,
  onConfirm,
  isOpen,
  mode,
  timeLimitText,
}: RecordingGuideModalProps) => {
  const [dontShowAgainModeList, setDontShowAgainModeList] = useState(
    getDontShowAgain()
  );

  const updateDontShowThisAgain = (e) => {
    const newStorage = e.target.checked
      ? [...dontShowAgainModeList, mode]
      : [...dontShowAgainModeList].filter((item) => item !== mode);
    setDontShowAgainModeList(newStorage);
  };

  return (
    <Modal
      onClose={onClose}
      containerClass="w-ds-md-modal"
      open={isOpen}
      noPadding
      title={
        <div className="flex h-8 pl-5 pt-5">
          <TextSubHeader
            label={
              mode === RecorderModes.updateRecording
                ? 'Continue cloning'
                : 'Cloning guide'
            }
          />
        </div>
      }
    >
      <div className="ds-md-modal">
        <div className="mt-14 flex flex-col">
          <div className="flex justify-center flex-row">
            <div className="flex flex-col items-center text-center justify-start ">
              {mode === RecorderModes.updateRecording ? (
                <>
                  <UpdateRecordingIllustration />
                  <div className="text-center px-18 mt-6">
                    <TextBase
                      color="text-ds-natural-600"
                      label="Visit the screens and perform the actions you’d like to update."
                    />
                    {timeLimitText}
                  </div>
                </>
              ) : (
                <>
                  <RecordingGuideIllustration />
                  <div className="mt-5 w-8/12">
                    <TextBase
                      color="text-ds-natural-600"
                      label={
                        'Go to any screen and action you want to show in your demo. When finished, hit "Stop cloning".'
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <label>
          <div className="flex flex-row pl-5 mb-12 items-center leading-4 mt-11">
            <input
              onChange={updateDontShowThisAgain}
              type="checkbox"
              className="border border-ds-natural-200 rounded-sm mr-1.5"
            />
            <TextBase
              color="text-ds-natural-600"
              label="Don’t show this message again"
            />
          </div>
        </label>
        <div className="flex flex-row-reverse pr-5 pb-5">
          <Button
            onClick={() => {
              localStorage.setItem(
                DONT_SHOW_AGAIN_KEY,
                JSON.stringify(dontShowAgainModeList)
              );
              onConfirm();
            }}
            label="Got it"
          />
        </div>
      </div>
    </Modal>
  );
};
